import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Info, MoreHorizontal, X } from "lucide-react";

/** Shared page furniture so every screen has the same header, notices and
 *  row menus — and all of them hold up on a phone. */

/** Title, one-line description and the page's own buttons. The buttons sit
 *  to the right on wide screens and wrap under the text on narrow ones. */
export function PageHeader({
  title, icon, description, actions, children,
}: {
  title: ReactNode;
  icon?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Extra lines under the description (badges, back links…). */
  children?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-xl sm:text-2xl font-semibold text-ink flex items-center gap-2 break-words">
          {icon}
          {title}
        </h1>
        {description && <p className="text-sm text-slate-500 mt-1 max-w-3xl">{description}</p>}
        {children}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 shrink-0">{actions}</div>}
    </header>
  );
}

const ALERT_TONES = {
  error: { box: "text-rose-800 bg-rose-50 border-rose-200", Icon: AlertTriangle, role: "alert" },
  success: { box: "text-emerald-800 bg-emerald-50 border-emerald-200", Icon: CheckCircle2, role: "status" },
  info: { box: "text-brand-900 bg-brand-50 border-brand-200", Icon: Info, role: "note" },
  warning: { box: "text-amber-900 bg-amber-50 border-amber-300", Icon: AlertTriangle, role: "note" },
} as const;

/** One notice style for errors, confirmations and hints. */
export function Alert({
  tone = "error", children, onDismiss, className = "",
}: {
  tone?: keyof typeof ALERT_TONES;
  children: ReactNode;
  onDismiss?: () => void;
  className?: string;
}) {
  const t = ALERT_TONES[tone];
  return (
    <div role={t.role} className={`mb-4 flex items-start gap-3 rounded-md border px-4 py-3 text-sm ${t.box} ${className}`}>
      <t.Icon className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1 break-words">{children}</div>
      {onDismiss && (
        <button type="button" onClick={onDismiss} className="-m-1 p-1 rounded opacity-70 hover:opacity-100" aria-label="Dismiss">
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export interface MenuItem {
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
  /** In-app route. */
  to?: string;
  /** External link (opens in a new tab). */
  href?: string;
  tone?: "danger";
  hidden?: boolean;
  disabled?: boolean;
}

/** "More" button for row actions that don't need to be on screen all the
 *  time. The list is placed with position:fixed so a scrolling table or a
 *  card with overflow can't clip it. */
const MENU_WIDTH = 220;

export function MoreMenu({ items, label = "More actions" }: { items: MenuItem[]; label?: string }) {
  const shown = items.filter((i) => !i.hidden);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left?: number; right?: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !btn.current) return;
    const r = btn.current.getBoundingClientRect();
    // Hang the list off the button's right edge, unless that would push it
    // past the left of the screen — then off its left edge.
    const x = r.right >= MENU_WIDTH + 8
      ? { right: Math.max(8, window.innerWidth - r.right) }
      : { left: Math.max(8, r.left) };
    const roomBelow = window.innerHeight - r.bottom;
    setPos(roomBelow < 40 * shown.length + 16 && r.top > roomBelow
      ? { bottom: window.innerHeight - r.top + 4, ...x }
      : { top: r.bottom + 4, ...x });
  }, [open, shown.length]);

  useEffect(() => {
    if (!open) return;
    list.current?.querySelector<HTMLElement>("[role=menuitem]:not([aria-disabled=true])")?.focus();
    const close = () => setOpen(false);
    const onDown = (e: MouseEvent) => {
      if (!list.current?.contains(e.target as Node) && !btn.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { close(); btn.current?.focus(); return; }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.preventDefault();
      const els = Array.from(list.current?.querySelectorAll<HTMLElement>("[role=menuitem]:not([aria-disabled=true])") ?? []);
      const i = els.indexOf(document.activeElement as HTMLElement);
      els[(i + (e.key === "ArrowDown" ? 1 : -1) + els.length) % els.length]?.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", close);
    // Any scroll moves the button away from the fixed list — just close.
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [open]);

  if (shown.length === 0) return null;
  const itemCls = (i: MenuItem) =>
    `flex w-full items-center gap-2 px-3 py-2 text-left text-sm min-h-[40px] focus:outline-none
     ${i.disabled ? "text-slate-400 cursor-not-allowed" : i.tone === "danger" ? "text-rose-700 hover:bg-rose-50 focus:bg-rose-50" : "text-slate-700 hover:bg-brand-50 focus:bg-brand-50"}`;

  return (
    <>
      <button
        ref={btn}
        type="button"
        className="btn-ghost !px-2"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => setOpen((o) => !o)}
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
      </button>
      {open && pos && (
        <div
          ref={list}
          role="menu"
          aria-label={label}
          className="fixed z-50 w-[220px] max-w-[calc(100vw-16px)] overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-elevated"
          style={pos}
        >
          {shown.map((i) => {
            const content = <>{i.icon}<span>{i.label}</span></>;
            const done = () => setOpen(false);
            if (i.to && !i.disabled) {
              return <Link key={i.label} role="menuitem" to={i.to} className={itemCls(i)} onClick={done}>{content}</Link>;
            }
            if (i.href && !i.disabled) {
              return <a key={i.label} role="menuitem" href={i.href} target="_blank" rel="noopener noreferrer" className={itemCls(i)} onClick={done}>{content}</a>;
            }
            return (
              <button
                key={i.label} type="button" role="menuitem" className={itemCls(i)}
                aria-disabled={i.disabled || undefined}
                onClick={() => { if (i.disabled) return; done(); i.onClick?.(); }}
              >
                {content}
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}
